/**
 * PostSessionTab Component
 * Contains Attendance, Coach Feedback, and Participant Feedback accordion sections.
 */

import { Stack, ThemeIcon } from '@mantine/core';
import { Check } from 'react-feather';
import { SectionAccordion } from './SectionAccordion';
import { AttendanceSection } from './AttendanceSection';
import { CoachFeedbackSection } from './CoachFeedbackSection';
import { ParticipantFeedbackSection } from './ParticipantFeedbackSection';

interface PostSessionTabProps {
  cohortId: string;
  sessionId: string;
  groupId?: string;
  attendanceMarked?: boolean;
  feedbackSubmitted?: boolean;
  onComplete?: () => void;
  onOpenFollowUps?: (options?: { sessionId?: string; participantId?: string }) => void;
  // MED-1078 (E1.22): when true (global flag AND per-modality LSD setting), the
  // attendance section opens read-only with a secondary override path.
  liveSessionDeliveryEnabled?: boolean;
}

export function PostSessionTab({
  cohortId,
  sessionId,
  groupId,
  attendanceMarked,
  feedbackSubmitted,
  onComplete,
  onOpenFollowUps,
  liveSessionDeliveryEnabled = false,
}: PostSessionTabProps) {
  return (
    <Stack gap="sm">
      <SectionAccordion
        title="Attendance"
        defaultExpanded
        rightSection={
          attendanceMarked ? (
            <ThemeIcon size="xs" variant="filled" color="green" radius="xl">
              <Check size={10} />
            </ThemeIcon>
          ) : undefined
        }
      >
        <AttendanceSection
          cohortId={cohortId}
          sessionId={sessionId}
          onComplete={onComplete}
          onOpenFollowUps={onOpenFollowUps}
          liveSessionDeliveryEnabled={liveSessionDeliveryEnabled}
        />
      </SectionAccordion>

      <SectionAccordion
        title="Coach Feedback"
        defaultExpanded={false}
        rightSection={
          feedbackSubmitted ? (
            <ThemeIcon size="xs" variant="filled" color="green" radius="xl">
              <Check size={10} />
            </ThemeIcon>
          ) : undefined
        }
      >
        <CoachFeedbackSection
          cohortId={cohortId}
          sessionId={sessionId}
          onFlagAddressed={onComplete}
        />
      </SectionAccordion>

      <SectionAccordion title="Participant Feedback" defaultExpanded={false}>
        <ParticipantFeedbackSection
          cohortId={cohortId}
          sessionId={sessionId}
          groupId={groupId}
        />
      </SectionAccordion>
    </Stack>
  );
}

export default PostSessionTab;
